import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import * as signalR from '@microsoft/signalr';
import { environment } from 'environments/environment';
import { MsalService } from '@azure/msal-angular';

export type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'reconnecting';

@Injectable({
  providedIn: 'root',
})
export class SignalRService implements OnDestroy {
  private connection: signalR.HubConnection;

  connectionState$ = new BehaviorSubject<ConnectionState>('disconnected');
  reconnected$ = new Subject<void>();

  constructor(private msalService: MsalService) {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && this.connection?.state === signalR.HubConnectionState.Disconnected) {
        this.start().catch(() => {});
      }
    });
  }

  getConnection(): signalR.HubConnection {
    if (!this.connection) {
      this.connection = new signalR.HubConnectionBuilder()
        .withUrl(environment.apiURL + '/hubs/board', {
          accessTokenFactory: () => this.getAccessToken(),
        })
        .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
        .build();

      this.connection.onreconnecting(() => {
        this.connectionState$.next('reconnecting');
      });

      this.connection.onreconnected(() => {
        this.connectionState$.next('connected');
        this.reconnected$.next();
      });

      this.connection.onclose(() => {
        this.connectionState$.next('disconnected');
      });
    }

    return this.connection;
  }

  async start(): Promise<void> {
    const conn = this.getConnection();
    if (conn.state === signalR.HubConnectionState.Disconnected) {
      this.connectionState$.next('connecting');
      try {
        await conn.start();
        this.connectionState$.next('connected');
      } catch (err) {
        this.connectionState$.next('disconnected');
        throw err;
      }
    }
  }

  async stop(): Promise<void> {
    if (this.connection) {
      await this.connection.stop();
      this.connectionState$.next('disconnected');
    }
  }

  private async getAccessToken(): Promise<string> {
    const account = this.msalService.instance.getActiveAccount()
      ?? this.msalService.instance.getAllAccounts()[0];

    if (!account) {
      return '';
    }

    try {
      const result = await this.msalService.instance.acquireTokenSilent({
        scopes: [environment.accessAsUserScope],
        account,
      });
      return result.accessToken;
    } catch {
      return '';
    }
  }

  ngOnDestroy(): void {
    this.stop();
  }
}
